/**
 * Error fallback component for error boundary
 * 
 * @author Claude
 */
import { Button, Container, Paper, Typography, Box } from '@mui/material';
import { FallbackProps } from 'react-error-boundary';

/**
 * Error fallback UI when an error is caught by ErrorBoundary
 * @param {FallbackProps} props - Props from ErrorBoundary
 * @returns {JSX.Element} Error fallback component
 * 
 * @a11y Properly announces error state
 * @a11y Provides clear recovery options
 */
export const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  return (
    <Container maxWidth="md" sx={{ mt: 8 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
        role="alert"
        aria-live="assertive"
      >
        <Typography variant="h4" component="h1" color="error" gutterBottom>
          Something went wrong
        </Typography>
        
        <Typography variant="body1" paragraph>
          We apologize for the inconvenience. The application has encountered an unexpected error.
        </Typography>
        
        <Box 
          sx={{ 
            p: 2, 
            bgcolor: 'grey.100', 
            borderRadius: 1, 
            width: '100%',
            mb: 3,
            overflowX: 'auto',
          }}
        >
          <Typography variant="body2" color="error.dark" sx={{ fontFamily: 'monospace' }}>
            {error.message || 'Unknown error'}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button 
            variant="outlined" 
            onClick={() => window.location.href = '/'}
          >
            Go to Home
          </Button>
          
          <Button 
            variant="contained" 
            onClick={resetErrorBoundary}
            color="primary"
          >
            Try Again
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ErrorFallback;